import { APIClient } from '@innerhive-internal/innerhive-api-client';
import { ILink, WithLinks } from '../../caremap/WithLinks';
import { ICareMapNoteTarget } from './ICareMapNoteTarget';
import { ILightUser } from '../../user/UserState';
import { IRecording } from '../../appointments/recording/RecordingState';
import { IAttachment } from '../generic-notes/attachments/IAttachment';

const careMapNoteMediaType = 'application/vnd.innerhive.caremap-note+json';
const careMapNoteHeaders = { accept: careMapNoteMediaType, 'content-type': careMapNoteMediaType };

export interface INoteReactions {
    [reaction: string]: {
        people: {
            name: string;
            isMe: boolean;
        }[];
    }
}

type ISO8601 = string;
export interface ICaremapNoteResponse extends ICaremapNoteRequest {
    createdAt: ISO8601
    createdBy: string;
    createdByUser?: ILightUser;
    updatedAt: ISO8601
    updatedBy: string;
    version: number;
    links: any;
    reactions: INoteReactions;
}

interface ICaremapNoteRequest {
    noteId: string;
    html: string;
    isPrivate?: boolean;
    title: string;
    attachments: IAttachment[];

    otherAttributes: {
        // careMapKey also lives in here but that is managed by (and ignored by) the api
        noteTarget: ICareMapNoteTarget;
        parentNoteId?: string;
        recording?: Partial<IRecording>;
    }

    version?: number;
}

export type ICaremapNote = ICaremapNoteRequest & Partial<ICaremapNoteResponse>;

export class NotesClient {
    static inject = () => [APIClient];
    constructor(private apiClient: APIClient){}

    getNotes = async (parentLink: ILink): Promise<WithLinks<ICaremapNoteResponse>[]> => {
        const notes = await this.apiClient.get(parentLink, careMapNoteMediaType);
        return notes;
    };

    getNote = async (selfLink: ILink): Promise<WithLinks<ICaremapNoteResponse>> => {
        return await this.apiClient.get(selfLink, careMapNoteMediaType);
    };

    addNote = async ({ parentLink, note }: { parentLink: ILink; note: ICaremapNoteRequest & { isComplete?: boolean }; }): Promise<WithLinks<ICaremapNoteResponse>> => {
        return await this.apiClient.post(parentLink, note, careMapNoteHeaders);
    };

    updateNote = async (note: WithLinks<ICaremapNoteRequest>) => {
        return await this.apiClient.put(note.links.self, note, careMapNoteHeaders);
    };

    deleteNote = async (note: WithLinks<ICaremapNoteRequest>) => {
        await this.apiClient.delete(note.links.self, {});
    };

    getNoteReactions = async (note: WithLinks<ICaremapNoteRequest>) => {
        const { reactions } = await this.apiClient.get(note.links.reactions);
        return reactions;
    };
}
